import { __decorate } from "tslib";
import React from 'react';
import { colorHexReg } from 'choerodon-ui/dataset/validator/rules/typeMismatch';
import Icon from 'choerodon-ui/lib/icon';
import { observer } from 'mobx-react';
import { action, observable, runInAction } from 'mobx';
import defaultTo from 'lodash/defaultTo';
import isBoolean from 'lodash/isBoolean';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import round from 'lodash/round';
import pull from 'lodash/pull';
import concat from 'lodash/concat';
import classNames from 'classnames';
import TriggerField from '../trigger-field/TriggerField';
import autobind from '../_util/autobind';
import EventManager from '../_util/EventManager';
import { transformZoomData } from '../_util/DocumentUtils';
import { FieldType } from '../data-set/enum';
import { $l } from '../locale-context';
import { ViewMode, ColorUnit } from './enum';
import { defaultColorMap, commonColorMap } from './presetColorMap';
const FILL_COLOR = '#FFFFFF';
const COLOR_LINE_LENGTH = 9;
const COLOR_STORAGE_ITEM = 'color.picker.customized.used';
// 相对亮度计算公式：L = 0.2126 * R + 0.7152 * G + 0.0722 * B
// 最高亮度：L = (0.2126 * 255 + 0.7152 * 255 + 0.0722 * 255) - 1.05
const COLOR_MAX_LIGHT = 253.95;
function getNodeRect(node) {
    return node.getBoundingClientRect();
}
let ColorPicker = class ColorPicker extends TriggerField {
    constructor(props, context) {
        super(props, context);
        this.eventManager = new EventManager(typeof window === 'undefined' ? undefined : document);
        this.footerInputRef = null;
        this.HSV = {
            h: 0,
            s: 1,
            v: 1,
            a: 1,
        };
        this.RGBA = {
            r: 255,
            g: 0,
            b: 0,
            a: 1,
        };
        this.saveGradientRef = node => (this.gradient = node);
        this.saveSelectPointerRef = node => (this.selectPointer = node);
        this.saveHuePointerRef = node => (this.huePointer = node);
        this.saveHueRef = node => (this.hue = node);
        this.saveOpacityRef = node => (this.opacity = node);
        this.saveOpacityPointerRef = node => (this.opacityPointer = node);
        this.saveFooterAlphaRef = node => (this.footerInputRef = node);
        runInAction(() => {
            this.gradientHidden = true;
        });
        this.popupView = null;
    }
    get multiple() {
        return false;
    }
    get range() {
        return false;
    }
    get preset() {
        const { preset } = this.props;
        if (isBoolean(preset)) {
            return preset;
        }
        return this.getContextConfig('colorPreset');
    }
    get defaultValidationMessages() {
        const label = this.getProp('label');
        return {
            valueMissing: $l('ColorPicker', label ? 'value_missing' : 'value_missing_no_label', {
                label,
            }),
            typeMismatch: $l('ColorPicker', 'type_mismatch'),
        };
    }
    get colorPickUsed() {
        const list = JSON.parse(localStorage.getItem(COLOR_STORAGE_ITEM) || '[]');
        if (list.length < COLOR_LINE_LENGTH) {
            return concat(list, Array(COLOR_LINE_LENGTH - list.length).fill(FILL_COLOR));
        }
        return list;
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'mode',
            'preset',
        ]);
    }
    getDefaultAction() {
        return this.getContextConfig('selectTrigger') || super.getDefaultAction();
    }
    componentDidUpdate() {
        const { popup } = this;
        if (popup) {
            const { h, s, v, a } = this.HSV;
            const { huePointer, opacityPointer, selectPointer, hue, opacity, gradient } = this;
            const { a: Alpha } = this.hsvToRGB(h, s, v, a);
            if (huePointer && hue) {
                const { width } = getNodeRect(hue);
                this.setSliderPointer((width * h) / 360, huePointer, hue, false);
            }
            if (opacityPointer && opacity) {
                const { width } = getNodeRect(opacity);
                this.setSliderPointer(width * Alpha, opacityPointer, opacity, false);
            }
            if (selectPointer && gradient) {
                const { width, height } = getNodeRect(gradient);
                const left = s * width;
                const top = height - v * height;
                this.setGradientPointer(left, top, selectPointer, gradient, false);
            }
        }
        if (!this.gradientHidden && this.popupView) {
            const { offsetTop } = this.popupView;
            const { left, top, width, height } = this.popupView.getBoundingClientRect();
            const { innerWidth, innerHeight } = window;
            runInAction(() => {
                if (left + width > innerWidth && left > width) {
                    this.gradienLeft = -width;
                }
                if (top + height > innerHeight) {
                    this.gradienTop = offsetTop - (top + height - innerHeight);
                }
            });
        }
    }
    getValue() {
        const value = super.getValue();
        return typeof value === 'string' ? value : undefined;
    }
    getBorder(r = 255, g = 0, b = 0) {
        return (0.2126 * r + 0.7152 * g + 0.0722 * b) > COLOR_MAX_LIGHT;
    }
    syncValueOnBlur(value) {
        const { footerEditFlag, gradientHidden, preset } = this;
        if (!footerEditFlag) {
            if (value !== '' && value[0] !== '#' && !value.startsWith('rgb') && !value.startsWith('hls')) {
                value = `#${value}`;
            }
            super.syncValueOnBlur(value);
        }
        if (preset && !gradientHidden) {
            this.setGradientHidden(true);
        }
    }
    getFieldType() {
        return FieldType.color;
    }
    getPrefix() {
        const { prefixCls, props: { mode, renderer } } = this;
        const backgroundColor = this.getValue();
        const isButtonMode = mode === ViewMode.button;
        if (isButtonMode && renderer) {
            return this.processRenderer(backgroundColor);
        }
        const { r, g, b } = backgroundColor && this.hexToRGB ? this.hexToRGB(backgroundColor) : this.RGBA;
        const className = classNames({
            [`${prefixCls}-color`]: !isButtonMode,
            [`${prefixCls}-button-color`]: isButtonMode,
            [`${prefixCls}-prefix-border`]: !isButtonMode && this.getBorder(r, g, b),
        });
        return (React.createElement("div", { className: `${prefixCls}-prefix` },
            React.createElement("span", { className: className, style: { backgroundColor } })));
    }
    getPopupFooter() {
        const { prefixCls, RGBA } = this;
        const className = `${prefixCls}-popup-footer-slider-pointer`;
        const huePointerProps = {
            onMouseDown: this.handleHPMouseDown,
            ref: this.saveHuePointerRef,
            className,
        };
        const opacityPointerProps = {
            onMouseDown: this.handleOpacityMouseDown,
            ref: this.saveOpacityPointerRef,
            className,
        };
        const inputProps = {
            onInput: this.handleFooterInput,
            onFocus: this.handleFooterFocus,
        };
        const { r, g, b, a } = RGBA;
        const value = (this.getEditorTextInfo().text || '').replace('#', '');
        return (React.createElement("div", { className: `${prefixCls}-popup-footer` },
            React.createElement("div", { className: `${prefixCls}-popup-footer-slide-bar` },
                React.createElement("div", null,
                    React.createElement("div", { ref: this.saveHueRef, className: `${prefixCls}-popup-footer-slider` },
                        React.createElement("div", { onClick: this.handleHueClick, className: "hue" }),
                        React.createElement("div", Object.assign({}, huePointerProps))),
                    React.createElement("div", { ref: this.saveOpacityRef, className: `${prefixCls}-popup-footer-slider` },
                        React.createElement("div", { onClick: this.handleOpacityClick, className: "opacity", style: { background: `linear-gradient(to right, rgba(${r}, ${g}, ${b}, 0), rgb(${r}, ${g}, ${b}))` } }),
                        React.createElement("div", Object.assign({}, opacityPointerProps)))),
                React.createElement("span", { className: `${prefixCls}-popup-footer-color`, style: { background: `rgba(${r}, ${g}, ${b}, ${a})` } })),
            React.createElement("div", { className: `${prefixCls}-popup-footer-input` },
                React.createElement("div", { className: `${prefixCls}-popup-footer-input-color` },
                    React.createElement("input", Object.assign({ name: ColorUnit.hex, value: value, autoComplete: "off" }, inputProps)),
                    React.createElement("span", null, ColorUnit.hex)),
                Object.keys(RGBA).map(item => {
                    return (React.createElement("div", { className: `${prefixCls}-popup-footer-input-color`, key: item },
                        React.createElement("input", Object.assign({ name: item, value: value ? this.RGBA[item] : '', autoComplete: "off", ref: item === 'a' ? this.saveFooterAlphaRef : undefined }, inputProps)),
                        React.createElement("span", null, ColorUnit[item])));
                }))));
    }
    getGradientPopupContent() {
        const { gradientHidden, prefixCls, preset } = this;
        const gradientProps = {
            className: `${prefixCls}-popup-body-gradient`,
            onClick: this.handleGPClick,
            ref: this.saveGradientRef,
        };
        const gradientPointerProps = {
            onMouseDown: this.handleGPMouseDown,
            ref: this.saveSelectPointerRef,
            className: `${prefixCls}-popup-body-selector`,
        };
        if (isEmpty(this.hueColor)) {
            this.setColor(this.getValue());
        }
        return (React.createElement("div", { ref: dom => { this.popupView = dom; }, className: `${prefixCls}-popup-view`, style: { display: preset && !gradientHidden || !preset ? 'block' : 'none', top: this.gradienTop, left: this.gradienLeft } },
            React.createElement("div", { className: `${prefixCls}-popup-view-picker-name` }, $l('ColorPicker', 'pick_color_view')),
            React.createElement("div", { className: `${prefixCls}-popup-body`, style: { backgroundColor: defaultTo(this.hueColor, '#ff0000') } },
                React.createElement("div", Object.assign({}, gradientProps)),
                React.createElement("div", Object.assign({}, gradientPointerProps))),
            this.getPopupFooter()));
    }
    getPresetData(list, value) {
        const { prefixCls, hexToRGB, getBorder } = this;
        const className = `${prefixCls}-popup-view-palettes`;
        const valueRgba = value ? hexToRGB(value) : { r: undefined, g: undefined, b: undefined, a: undefined };
        return (React.createElement("div", { className: className }, [...list].map((item, index) => {
            const { r, g, b, a } = hexToRGB(item);
            const border = getBorder(r, g, b);
            const { r: vr, g: vg, b: vb, a: va } = valueRgba;
            const active = r === vr && g === vg && b === vb && a === va;
            const key = `${item}_${index}`;
            return (React.createElement("div", { className: classNames(`${className}-block`, {
                    [`${className}-block-border`]: border,
                    [`${className}-block-active`]: active,
                }), style: { backgroundColor: item }, key: key, onClick: () => this.handlePreset(item) }, active && React.createElement(Icon, { type: "check" })));
        })));
    }
    getPopupContent() {
        const { prefixCls, preset } = this;
        if (preset) {
            const value = this.getValue();
            return (React.createElement("div", { className: `${prefixCls}-popup-view-preset` },
                React.createElement("div", { onClick: () => this.setGradientHidden(true) },
                    this.getPresetData(commonColorMap, value),
                    this.getPresetData(defaultColorMap, value),
                    React.createElement("div", { className: `${prefixCls}-popup-picker-name` }, $l('ColorPicker', 'used_view')),
                    this.getPresetData(this.colorPickUsed)),
                React.createElement("div", { className: `${prefixCls}-popup-view-gradient` },
                    React.createElement("div", { className: `${prefixCls}-popup-picker-name`, onClick: this.onExpandChange },
                        React.createElement("div", null,
                            React.createElement(Icon, { type: "color_lens-o" }),
                            React.createElement("span", null, $l('ColorPicker', 'custom_view'))),
                        React.createElement(Icon, { type: "navigate_next" })),
                    this.getGradientPopupContent())));
        }
        return this.getGradientPopupContent();
    }
    setHSV(h, s, v, a) {
        const { HSV } = this;
        if (h !== undefined && h !== HSV.h) {
            HSV.h = h;
        }
        if (v !== undefined && v !== HSV.v) {
            HSV.v = v;
        }
        if (s !== undefined && s !== HSV.s) {
            HSV.s = s;
        }
        if (a !== undefined && a !== HSV.a) {
            HSV.a = a;
        }
    }
    setRGBA(r, g, b, a) {
        const { RGBA } = this;
        if (isNil(r) || r > 255)
            r = 255;
        if (isNil(g) || g > 255)
            g = 255;
        if (isNil(b) || b > 255)
            b = 255;
        if (isNil(a) || a > 1)
            a = 1;
        if (r !== RGBA.r) {
            RGBA.r = round(r);
        }
        if (g !== RGBA.g) {
            RGBA.g = round(g);
        }
        if (b !== RGBA.b) {
            RGBA.b = round(b);
        }
        if (a !== RGBA.a) {
            RGBA.a = a;
        }
    }
    setHueColor(color) {
        if (color !== this.hueColor) {
            this.hueColor = color;
        }
    }
    setFooterEditFlag(footerEditFlag) {
        if (footerEditFlag !== this.footerEditFlag) {
            this.footerEditFlag = footerEditFlag;
        }
    }
    setGradientHidden(gradientHidden) {
        if (gradientHidden !== this.gradientHidden) {
            this.gradientHidden = gradientHidden;
        }
    }
    setColor(color) {
        if (!isNil(color) && color.slice(0, 1) === '#' && color.length > 3) {
            const { gradient, selectPointer, hue, huePointer, opacity, opacityPointer } = this;
            const { r, g, b, a } = this.hexToRGB(color);
            const { h, s, v } = this.rgbToHSV(r / 255, g / 255, b / 255, a);
            this.setHSV(h, s, v, a);
            this.setRGBA(r, g, b, a);
            const { r: hr, g: hg, b: hb, a: ha } = this.hsvToRGB(h, 1, 1, 1);
            const hueColor = this.rgbToHEX(hr, hg, hb, ha);
            this.setHueColor(hueColor);
            if (gradient && selectPointer && hue && huePointer) {
                const { height, width } = getNodeRect(gradient);
                const left = s * width;
                const top = height - v * height;
                const { width: hueWidth } = getNodeRect(hue);
                const { width: opacityWidth } = getNodeRect(opacity);
                const hueLeft = (h / 360) * hueWidth;
                const opacityLeft = a * opacityWidth;
                this.setSliderPointer(hueLeft, huePointer, hue, false);
                this.setSliderPointer(opacityLeft, opacityPointer, opacity, false);
                this.setGradientPointer(left, top, selectPointer, gradient, false);
            }
            if (this.getValue() !== color) {
                this.prepareSetValue(color);
            }
        }
    }
    positionToHSV(left, top, width, height) {
        const { h, a } = this.HSV;
        if (width < 0) {
            width = 0;
        }
        const s = left / width;
        const v = 1 - top / height;
        return { h, s, v, a };
    }
    rgbToHEX(r, g, b, a) {
        function hex(num) {
            const hexNum = Number(num).toString(16);
            return hexNum.length === 1 ? `0${hexNum}` : hexNum[1] === '.' ? `0${hexNum[0]}` : hexNum;
        }
        if (a !== 1) {
            return `#${hex(r)}${hex(g)}${hex(b)}${hex(round(a * 255))}`;
        }
        return `#${hex(r)}${hex(g)}${hex(b)}`;
    }
    hexToRGB(hex) {
        hex = hex.split('#')[1] || hex.split('#')[0];
        const length = hex.length;
        let results = '';
        const hexArray = hex.split('');
        if (length === 3 || length === 4) {
            for (let i = 0; i < length; i++) {
                results = `${results}${hexArray[i]}${hexArray[i]}`;
            }
        }
        else if (length === 5) {
            results = `${hex}${hexArray[length - 1]}`;
        }
        else {
            results = hex;
        }
        const hasAlpha = results.length === 8;
        results = results.slice(0, hasAlpha ? 8 : 6);
        const result = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})?$/i.exec(results);
        return result
            ? {
                r: parseInt(result[1], 16),
                g: parseInt(result[2], 16),
                b: parseInt(result[3], 16),
                a: hasAlpha ? round(parseInt(result[4], 16) / 255, 2) : 1,
            }
            : {
                r: 255,
                g: 0,
                b: 0,
                a: 1,
            };
    }
    rgbToHSV(r, g, b, a) {
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const v = max;
        let s;
        let h;
        if (v === 0) {
            s = 0;
        }
        else {
            s = (max - min) / max;
        }
        if (max === min) {
            h = 0;
        }
        else {
            const d = r === min ? g - b : b === min ? r - g : b - r;
            const m = r === min ? 3 : b === min ? 1 : 5;
            h = 60 * (m - d / (max - min));
        }
        if (h < 0) {
            h += 360;
        }
        return { h, s, v, a };
    }
    hsvToRGB(h, s, v, a) {
        h /= 60;
        const h1 = Math.floor(h);
        const f = h - h1;
        const p = v * (1 - s);
        const q = v * (1 - f * s);
        const t = v * (1 - (1 - f) * s);
        let rgb;
        switch (h1) {
            case 0:
                rgb = { r: v, g: t, b: p, a };
                break;
            case 1:
                rgb = { r: q, g: v, b: p, a };
                break;
            case 2:
                rgb = { r: p, g: v, b: t, a };
                break;
            case 3:
                rgb = { r: p, g: q, b: v, a };
                break;
            case 4:
                rgb = { r: t, g: p, b: v, a };
                break;
            default:
                rgb = { r: v, g: p, b: q, a };
                break;
        }
        rgb.r = Math.floor(rgb.r * 255);
        rgb.g = Math.floor(rgb.g * 255);
        rgb.b = Math.floor(rgb.b * 255);
        return rgb;
    }
    setGradientPointer(x, y, pointer, wrap, isClient) {
        const { left: wrapX, top: wrapY, width: wrapW, height: wrapH } = getNodeRect(wrap);
        const { height: pointerH, width: pointerW } = getNodeRect(pointer);
        let left;
        let top;
        if (isClient) {
            left = x - wrapX < 0 ? 0 : x - wrapX > wrapW ? wrapW : x - wrapX;
            top = y - wrapY < 0 ? 0 : y - wrapY > wrapH ? wrapH : y - wrapY;
        }
        else {
            left = x;
            top = y;
        }
        pointer.style.left = `${left - pointerH / 2}px`;
        pointer.style.top = `${top - pointerW / 2}px`;
        return { left, top };
    }
    handleGPClick(e) {
        const { gradient, selectPointer, setGradientPointer } = this;
        if (gradient && selectPointer) {
            const { positionToHSV, rgbToHEX, hsvToRGB } = this;
            const { left, top } = setGradientPointer(transformZoomData(e.clientX), transformZoomData(e.clientY), selectPointer, gradient, true);
            const { height, width } = getNodeRect(gradient);
            const { h, s, v, a: ha } = positionToHSV(left, top, width, height);
            this.setHSV(undefined, s, v, undefined);
            const { r, g, b, a } = hsvToRGB(h, s, v, ha);
            const hexColor = rgbToHEX(r, g, b, a);
            this.setRGBA(r, g, b, round(a, 2));
            this.prepareSetValue(hexColor);
        }
    }
    setSliderPointer(x, pointer, wrap, isClient) {
        const { left: wrapX, width: wrapW } = getNodeRect(wrap);
        const { width: pointerW } = getNodeRect(pointer);
        let left;
        if (isClient) {
            left = x - wrapX < 0 ? 0 : x - wrapX > wrapW ? wrapW : x - wrapX;
        }
        else {
            left = x;
        }
        pointer.style.left = `${left - pointerW / 2}px`;
        return { left, wrapW };
    }
    handleHueClick(e) {
        const { hue, huePointer, setSliderPointer, hsvToRGB, rgbToHEX } = this;
        if (hue && huePointer) {
            const { left, wrapW } = setSliderPointer(transformZoomData(e.clientX), huePointer, hue, true);
            const h = Math.floor((left / wrapW) * 360);
            const { s, v, a: ha } = this.HSV;
            this.setHSV(h, undefined, undefined, undefined);
            const { r, g, b, a } = hsvToRGB(h, 1, 1, 1);
            const { r: valueR, g: valueG, b: valueB, a: valueA } = hsvToRGB(h, s, v, ha);
            const hueColor = rgbToHEX(r, g, b, a);
            const valueColor = rgbToHEX(valueR, valueG, valueB, valueA);
            this.setRGBA(valueR, valueG, valueB, valueA);
            this.setHueColor(hueColor);
            this.prepareSetValue(valueColor);
        }
    }
    handleOpacityClick(e) {
        const { opacity, opacityPointer, setSliderPointer, hsvToRGB, rgbToHEX } = this;
        if (opacity && opacityPointer) {
            const { left, wrapW } = setSliderPointer(transformZoomData(e.clientX), opacityPointer, opacity, true);
            const a = round(left / wrapW, 2);
            const { h, s, v } = this.HSV;
            const { r, g, b } = hsvToRGB(h, s, v, a);
            this.setHSV(h, s, v, a);
            this.setRGBA(r, g, b, a);
            this.prepareSetValue(rgbToHEX(r, g, b, a));
        }
    }
    handleFooterInput(e) {
        const { target, target: { name } } = e;
        let { value } = target;
        const rgba = this.RGBA;
        if (name === ColorUnit.hex) {
            value = `#${value}`;
            this.prepareSetValue(value);
            if (colorHexReg.test(value)) {
                const { r, g, b, a } = this.hexToRGB(value);
                const { h, s, v } = this.rgbToHSV(r / 255, g / 255, b / 255, a);
                const { r: hr, g: hg, b: hb, a: ha } = this.hsvToRGB(h, 1, 1, 1);
                this.setRGBA(r, g, b, a);
                this.setHSV(h, s, v, a);
                this.setHueColor(this.rgbToHEX(hr, hg, hb, ha));
            }
        }
        else {
            let valueToNumber = Number(value);
            if (!isNaN(valueToNumber) || name === 'a') {
                if (name === 'a') {
                    if (valueToNumber >= 1) {
                        valueToNumber = 1;
                    }
                    else if (value && value.slice(-1) === '.') {
                        const timer = setTimeout(() => {
                            clearTimeout(timer);
                            if (this.footerInputRef) {
                                this.footerInputRef.value = value;
                            }
                        }, 0);
                        return;
                    }
                }
                else if (value > 255) {
                    valueToNumber = 255;
                }
                rgba[name] = valueToNumber;
                const { r, g, b, a } = rgba;
                const { h, s, v } = this.rgbToHSV(r / 255, g / 255, b / 255, a);
                this.setRGBA(r, g, b, a);
                this.setHSV(h, s, v, a);
                this.prepareSetValue(this.rgbToHEX(r, g, b, a));
            }
        }
    }
    handleGPMouseDown() {
        this.eventManager
            .addEventListener('mousemove', this.handleGPClick)
            .addEventListener('mouseup', this.onGPMouseUp);
    }
    onGPMouseUp() {
        this.eventManager
            .removeEventListener('mousemove', this.handleGPClick)
            .removeEventListener('mouseup', this.onGPMouseUp);
    }
    handleHPMouseDown() {
        this.eventManager
            .addEventListener('mousemove', this.handleHueClick)
            .addEventListener('mouseup', this.onHPMouseUp);
    }
    handleOpacityMouseDown() {
        this.eventManager
            .addEventListener('mousemove', this.handleOpacityClick)
            .addEventListener('mouseup', this.onOpacityMouseUp);
    }
    onHPMouseUp() {
        this.eventManager
            .removeEventListener('mousemove', this.handleHueClick)
            .removeEventListener('mouseup', this.onHPMouseUp);
    }
    onOpacityMouseUp() {
        this.eventManager
            .removeEventListener('mousemove', this.handleOpacityClick)
            .removeEventListener('mouseup', this.onOpacityMouseUp);
    }
    onExpandChange(e) {
        e.stopPropagation();
        this.setGradientHidden(!this.gradientHidden);
        this.forceUpdate();
    }
    handleFocus(e) {
        this.setFooterEditFlag(false);
        super.handleFocus(e);
    }
    handleFooterFocus() {
        this.setFooterEditFlag(true);
    }
    setPopup(statePopup) {
        super.setPopup(statePopup);
        const value = this.getValue();
        if (value && statePopup) {
            this.setColor(value);
        }
        else if (value && colorHexReg.test(value)) {
            let { colorPickUsed } = this;
            const valueIndex = colorPickUsed.indexOf(value);
            if (valueIndex === -1) {
                colorPickUsed = [...new Set([value, ...colorPickUsed])].slice(0, COLOR_LINE_LENGTH);
            }
            else {
                colorPickUsed = [...new Set([value, ...pull(colorPickUsed, value)])];
            }
            localStorage.setItem(COLOR_STORAGE_ITEM, JSON.stringify(colorPickUsed));
        }
    }
    handlePreset(color) {
        this.setColor(color);
        this.setPopup(false);
    }
    handlePopupAnimateAppear() {
        this.setColor(this.getValue());
    }
    handlePopupAnimateEnd() {
        // noop
    }
    getPopupStyleFromAlign() {
        return undefined;
    }
    getTriggerIconFont() {
        const { mode } = this.props;
        if (mode !== ViewMode.button) {
            return 'palette';
        }
        return '';
    }
    getWrapperClassNames() {
        const { prefixCls } = this;
        return super.getWrapperClassNames({
            [`${prefixCls}-button`]: this.props.mode === ViewMode.button,
        });
    }
    renderLengthInfo() {
        return undefined;
    }
};
ColorPicker.displayName = 'ColorPicker';
ColorPicker.defaultProps = {
    ...TriggerField.defaultProps,
    suffixCls: 'color-picker',
    clearButton: false,
    mode: ViewMode.default,
};
__decorate([
    observable
], ColorPicker.prototype, "hueColor", void 0);
__decorate([
    observable
], ColorPicker.prototype, "footerEditFlag", void 0);
__decorate([
    observable
], ColorPicker.prototype, "gradientHidden", void 0);
__decorate([
    observable
], ColorPicker.prototype, "gradienLeft", void 0);
__decorate([
    observable
], ColorPicker.prototype, "gradienTop", void 0);
__decorate([
    action
], ColorPicker.prototype, "setHueColor", null);
__decorate([
    action
], ColorPicker.prototype, "setFooterEditFlag", null);
__decorate([
    action
], ColorPicker.prototype, "setGradientHidden", null);
__decorate([
    autobind
], ColorPicker.prototype, "setColor", null);
__decorate([
    autobind
], ColorPicker.prototype, "positionToHSV", null);
__decorate([
    action
], ColorPicker.prototype, "hexToRGB", null);
__decorate([
    autobind
], ColorPicker.prototype, "setGradientPointer", null);
__decorate([
    autobind
], ColorPicker.prototype, "handleGPClick", null);
__decorate([
    autobind
], ColorPicker.prototype, "setSliderPointer", null);
__decorate([
    autobind
], ColorPicker.prototype, "handleHueClick", null);
__decorate([
    autobind
], ColorPicker.prototype, "handleOpacityClick", null);
__decorate([
    autobind
], ColorPicker.prototype, "handleFooterInput", null);
__decorate([
    autobind
], ColorPicker.prototype, "handleGPMouseDown", null);
__decorate([
    autobind
], ColorPicker.prototype, "onGPMouseUp", null);
__decorate([
    autobind
], ColorPicker.prototype, "handleHPMouseDown", null);
__decorate([
    autobind
], ColorPicker.prototype, "handleOpacityMouseDown", null);
__decorate([
    autobind
], ColorPicker.prototype, "onHPMouseUp", null);
__decorate([
    autobind
], ColorPicker.prototype, "onOpacityMouseUp", null);
__decorate([
    autobind
], ColorPicker.prototype, "onExpandChange", null);
__decorate([
    autobind
], ColorPicker.prototype, "handleFocus", null);
__decorate([
    autobind
], ColorPicker.prototype, "handleFooterFocus", null);
__decorate([
    action
], ColorPicker.prototype, "setPopup", null);
__decorate([
    autobind
], ColorPicker.prototype, "handlePreset", null);
__decorate([
    autobind
], ColorPicker.prototype, "handlePopupAnimateAppear", null);
ColorPicker = __decorate([
    observer
], ColorPicker);
export default ColorPicker;
